import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import Dashboard from "@/pages/Dashboard";
import AppDetail from "@/pages/AppDetail";
import CommandCenter from "@/pages/CommandCenter";
import CommandDesk from "@/pages/CommandDesk";
import EnterpriseRegistry from "@/pages/EnterpriseRegistry";
import EnterpriseTasks from "@/pages/EnterpriseTasks";
import AdminAccessCenter from "@/pages/AdminAccessCenter";
import Companies from "@/pages/Companies";
import CompanyDashboard from "@/pages/CompanyDashboard";
import Approvals from "@/pages/Approvals";
import ApprovalPolicies from "@/pages/ApprovalPolicies";
import SiteEditor from "@/pages/SiteEditor";
import FixCenter from "@/pages/FixCenter";
import KpiRisk from "@/pages/KpiRisk";
import VaelithonReign from "@/pages/VaelithonReign";
import DeveloperMode from "@/pages/DeveloperMode";
import AuditLog from "@/pages/AuditLog";
import { Loader2 } from "lucide-react";

const FullscreenLoader = () => (
  <div className="min-h-screen bg-obsidian flex items-center justify-center">
    <Loader2 className="w-6 h-6 text-champagne animate-spin" />
  </div>
);

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <FullscreenLoader />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

const AdminRoute = ({ children, permission = null }) => {
  const { user, admin, hasPermission, loading } = useAuth();
  if (loading) return <FullscreenLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (!admin) return <Navigate to="/" replace />;
  if (permission && !hasPermission(permission)) return <Navigate to="/" replace />;
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <FullscreenLoader />;
  if (user) return <Navigate to="/" replace />;
  return children;
};

// Root: admins land on Command Center, others land on original Dashboard
const RootRedirect = () => {
  const { admin, bootstrapAvailable } = useAuth();
  if (admin || bootstrapAvailable) return <CommandCenter />;
  return <Dashboard />;
};

const App = () => (
  <div className="App dark">
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
          <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
          <Route path="/forgot" element={<PublicRoute><ForgotPassword /></PublicRoute>} />

          <Route path="/" element={<ProtectedRoute><RootRedirect /></ProtectedRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/apps/:appId" element={<ProtectedRoute><AppDetail /></ProtectedRoute>} />

          <Route path="/desk" element={<AdminRoute><CommandDesk /></AdminRoute>} />
          <Route path="/companies" element={<AdminRoute><Companies /></AdminRoute>} />
          <Route path="/companies/:entityId" element={<AdminRoute><CompanyDashboard /></AdminRoute>} />
          <Route path="/enterprise" element={<AdminRoute><EnterpriseRegistry /></AdminRoute>} />
          <Route path="/enterprise/tasks" element={<AdminRoute><EnterpriseTasks /></AdminRoute>} />
          <Route path="/approvals" element={<AdminRoute permission="approvals.view"><Approvals /></AdminRoute>} />
          <Route path="/approvals/policies" element={<AdminRoute permission="admins.manage"><ApprovalPolicies /></AdminRoute>} />
          <Route path="/editor" element={<AdminRoute><SiteEditor /></AdminRoute>} />
          <Route path="/fix-center" element={<AdminRoute><FixCenter /></AdminRoute>} />
          <Route path="/kpi-risk" element={<AdminRoute><KpiRisk /></AdminRoute>} />
          <Route path="/admins" element={<AdminRoute permission="admins.view"><AdminAccessCenter /></AdminRoute>} />
          <Route path="/vaelithon" element={<AdminRoute permission="integrations.view"><VaelithonReign /></AdminRoute>} />
          {/* Legacy path — kept so existing bookmarks/tutorials still work */}
          <Route path="/nexaura" element={<Navigate to="/vaelithon" replace />} />
          <Route path="/audit" element={<AdminRoute permission="audit.view"><AuditLog /></AdminRoute>} />
          <Route path="/developer" element={<AdminRoute permission="security.view"><DeveloperMode /></AdminRoute>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster
          theme="dark"
          position="top-right"
          toastOptions={{
            style: {
              background: "#121215",
              border: "1px solid #27272A",
              color: "#F8F9FA",
            },
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  </div>
);

export default App;
